import type { SessionLanguage } from './competencyAssessment';

export type CATranslations = {
  // shared nav
  title: string;
  allSessions: string;
  newSession: string;
  backToSessions: string;
  sessionLabel: string;
  loading: string;
  // index.tsx
  startSession: string;
  starting: string;
  clickToStart: string;
  // [id].tsx — chat
  loadingSession: string;
  typePlaceholder: string;
  send: string;
  evaluatingChat: string;
  coverageTitle: string;
  turnLabel: string;
  evidenceLabel: string;
  emptyTurnsLabel: string;
  // result/[id].tsx
  assessmentResult: string;
  evaluatingResult: string;
  failedTitle: string;
  failedBody: string;
  goToSessions: string;
  notCompleted: string;
  continueSession: string;
  insufficientEvidence: string;
  insufficientEvidenceDesc: string;
  levelFallback: string;
  certaintyPrefix: string;
  evidencePrefix: string;
  sectionJustification: string;
  sectionBehavioral: string;
  sectionMissing: string;
  sectionRecommendation: string;
  sectionTranscript: string;
  messages: string;
  confidenceLabels: Record<string, string>;
  evidenceLabels: Record<string, string>;
  levelDescriptions: Record<number, string>;
};

export const CA_TRANSLATIONS: Record<SessionLanguage, CATranslations> = {
  pl: {
    title: 'Ocena kompetencji',
    allSessions: 'Wszystkie sesje',
    newSession: 'Nowa sesja',
    backToSessions: '← Wszystkie sesje',
    sessionLabel: 'Sesja',
    loading: 'Ładowanie…',
    startSession: 'Rozpocznij sesję',
    starting: 'Uruchamianie…',
    clickToStart: 'Kliknij „Rozpocznij sesję", aby zacząć ocenę kompetencji.',
    loadingSession: 'Ładowanie sesji…',
    typePlaceholder: 'Wpisz odpowiedź…',
    send: 'Wyślij',
    evaluatingChat: 'Trwa ocena Twoich odpowiedzi…',
    coverageTitle: 'Stan pokrycia',
    turnLabel: 'Tura',
    evidenceLabel: 'Dowody',
    emptyTurnsLabel: 'Puste tury',
    assessmentResult: 'Wynik oceny',
    evaluatingResult:
      'Trwa ocena Twoich odpowiedzi — wyniki pojawią się za chwilę.',
    failedTitle: 'Ocena nie powiodła się',
    failedBody: 'Wystąpił błąd podczas analizy Twoich odpowiedzi.',
    goToSessions: 'Przejdź do wszystkich sesji',
    notCompleted: 'Ta sesja nie została jeszcze ukończona.',
    continueSession: 'Kontynuuj sesję',
    insufficientEvidence: 'Niewystarczające dowody',
    insufficientEvidenceDesc: 'Za mało danych, aby określić poziom',
    levelFallback: 'Poziom',
    certaintyPrefix: 'Pewność',
    evidencePrefix: 'Dowody',
    sectionJustification: 'Uzasadnienie',
    sectionBehavioral: 'Wykryte wskaźniki behawioralne',
    sectionMissing: 'Brakujące informacje',
    sectionRecommendation: 'Rekomendacja rozwojowa',
    sectionTranscript: 'Transkrypt rozmowy',
    messages: 'wiadomości',
    confidenceLabels: {
      high: 'Wysoka',
      'medium-high': 'Średnio-wysoka',
      medium: 'Średnia',
      low: 'Niska',
      insufficient: 'Niewystarczająca',
    },
    evidenceLabels: {
      strong: 'Mocne',
      supported: 'Poparte',
      weak: 'Słabe',
      insufficient: 'Niewystarczające',
    },
    levelDescriptions: {
      1: 'Początkujący — wykazuje podstawową świadomość, wymaga znaczącego rozwoju',
      2: 'Rozwijający się — przejawia pewne kompetencje, potrzebuje wsparcia i wskazówek',
      3: 'Biegły — konsekwentnie demonstruje kompetencję samodzielnie',
      4: 'Zaawansowany — przekracza oczekiwania, wpływa na innych i ich coachuje',
      5: 'Ekspert — uznany autorytet, napędza strategiczne zmiany na najwyższym poziomie',
    },
  },

  en: {
    title: 'Competency Assessment',
    allSessions: 'All sessions',
    newSession: 'New session',
    backToSessions: '← All sessions',
    sessionLabel: 'Session',
    loading: 'Loading…',
    startSession: 'Start session',
    starting: 'Starting…',
    clickToStart: 'Click "Start session" to begin the competency assessment.',
    loadingSession: 'Loading session…',
    typePlaceholder: 'Type your answer…',
    send: 'Send',
    evaluatingChat: 'Evaluating your responses…',
    coverageTitle: 'Coverage',
    turnLabel: 'Turn',
    evidenceLabel: 'Evidence',
    emptyTurnsLabel: 'Empty turns',
    assessmentResult: 'Assessment result',
    evaluatingResult:
      'Evaluating your responses — results will appear shortly.',
    failedTitle: 'Assessment failed',
    failedBody: 'An error occurred while analysing your responses.',
    goToSessions: 'Go to all sessions',
    notCompleted: 'This session has not been completed yet.',
    continueSession: 'Continue session',
    insufficientEvidence: 'Insufficient evidence',
    insufficientEvidenceDesc: 'Not enough data to determine a level',
    levelFallback: 'Level',
    certaintyPrefix: 'Confidence',
    evidencePrefix: 'Evidence',
    sectionJustification: 'Justification',
    sectionBehavioral: 'Detected behavioural indicators',
    sectionMissing: 'Missing information',
    sectionRecommendation: 'Development recommendation',
    sectionTranscript: 'Conversation transcript',
    messages: 'messages',
    confidenceLabels: {
      high: 'High',
      'medium-high': 'Medium-high',
      medium: 'Medium',
      low: 'Low',
      insufficient: 'Insufficient',
    },
    evidenceLabels: {
      strong: 'Strong',
      supported: 'Supported',
      weak: 'Weak',
      insufficient: 'Insufficient',
    },
    levelDescriptions: {
      1: 'Beginner — shows basic awareness, requires significant development',
      2: 'Developing — demonstrates some competency, needs support and guidance',
      3: 'Proficient — consistently demonstrates competency independently',
      4: 'Advanced — exceeds expectations, influences and coaches others',
      5: 'Expert — recognised authority, drives strategic change at the highest level',
    },
  },
};

export const getCATranslations = (
  language: SessionLanguage = 'pl'
): CATranslations => CA_TRANSLATIONS[language];
